import { useState, useEffect } from 'react';
import {
  Box, Chip, CircularProgress, IconButton, Typography,
  Autocomplete, TextField, Stack, Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { getUserNodes, addUserNode, removeUserNode } from '../api/users';
import { getNodes } from '../api/nodes';
import type { UserNodeInfo, NodeResponse } from '../api/types';

interface Props {
  userId: string;
}

export function UserNodePanel({ userId }: Props) {
  const [nodes, setNodes] = useState<UserNodeInfo[]>([]);
  const [allNodes, setAllNodes] = useState<NodeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const loadNodes = async () => {
    try {
      const [userNodesRes, allNodesRes] = await Promise.all([
        getUserNodes(userId),
        getNodes(),
      ]);
      setNodes(userNodesRes.data || []);
      setAllNodes((allNodesRes.data || []).filter(n => n.isActive));
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadNodes(); }, [userId]);

  const handleAdd = async (nodeId: string) => {
    if (!nodeId) return;
    setAdding(true);
    try {
      await addUserNode(userId, nodeId);
      await loadNodes();
    } catch { /* ignore */ }
    setAdding(false);
  };

  const handleRemove = async (nodeId: string) => {
    try {
      await removeUserNode(userId, nodeId);
      setNodes(prev => prev.filter(n => n.nodeId !== nodeId));
    } catch { /* ignore */ }
  };

  // 可添加的节点（排除已分配的）
  const assignedIds = new Set(nodes.map(n => n.nodeId));
  const availableNodes = allNodes.filter(n => !assignedIds.has(n.id));

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
        已分配节点 ({nodes.length})
      </Typography>

      {nodes.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mb: 2 }}>
          {nodes.map(node => (
            <Chip
              key={node.assignmentId}
              label={node.nodeName}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => handleRemove(node.nodeId)}
              deleteIcon={
                <Tooltip title="移除分配">
                  <DeleteIcon fontSize="small" />
                </Tooltip>
              }
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          暂无分配节点
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Autocomplete
          size="small"
          options={availableNodes}
          getOptionLabel={(node) => `${node.name} (${node.address}:${node.port})`}
          onChange={(_, val) => { if (val) handleAdd(val.id); }}
          disabled={adding}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="添加节点..."
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: <AddIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />,
              }}
            />
          )}
          sx={{ minWidth: 260, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
          noOptionsText="无可添加的节点"
        />
      </Box>
    </Box>
  );
}
